/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Página: Orders » Detalhe do Pedido
 * ---------------------------------------------------------------------------
 * - Mostra cabeçalho + linhas
 * - Ações: Avaliar/Planejar e Liberar
 * - Link "Voltar" para a lista de pedidos
 * ========================================================================== */

import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getOrder, evaluateOrder, releaseOrder, type Order } from "@/api/orders";

/** Helpers tolerantes ao schema ------------------------------------------------ */
function getRefCode(o: Order): string {
    const a = o as any;
    return String(a.ref_code ?? a.reference ?? a.code ?? a.id ?? "");
}
function getStatus(o: Order): string {
    const a = o as any;
    return String(a.status ?? "");
}
function getCustomerDisplay(o: Order): string {
    const a = o as any;
    return (
        a.customer_name ??
        a.customer?.name ??
        a.customer_name1 ??
        a.customer ??
        ""
    );
}
function getCreatedAt(o: Order): string | undefined {
    const a = o as any;
    return a.created_at ?? a.created ?? a.createdAt ?? a.created_on ?? undefined;
}
function getRequireDate(o: Order): string | undefined {
    const a = o as any;
    return (
        a.require_date ??
        a.required_date ??
        a.request_date ??
        a.requested_date ??
        a.due_date ??
        undefined
    );
}

/** Linha normalizada apenas para render (campos opcionais) */
type NormLine = {
    id?: number | string;
    product_code?: string;
    product_name?: string;
    uom?: string;
    qty?: number | string;
    lot?: string;
    expiry_date?: string;
};

/** Tenta mapear qualquer array de linhas conhecido para o formato NormLine[] */
function normalizeLines(o: Order): NormLine[] {
    const a = o as any;
    const raw: any[] =
        a.lines ??
        a.items ??
        a.order_lines ??
        a.details ??
        a.positions ??
        [];

    return (Array.isArray(raw) ? raw : []).map((l: any): NormLine => {
        const product_code =
            l.product_code ??
            l.productCode ??
            l.product_code1 ??
            l.sku ??
            l.product?.code ??
            l.product ??
            undefined;

        const product_name =
            l.product_name ??
            l.productName ??
            l.description ??
            l.product?.name ??
            undefined;

        const uom = l.uom ?? l.uom_code ?? l.unit ?? l.unit_code ?? undefined;

        const qty =
            l.qty ?? l.quantity ?? l.request_qty ?? l.requested_qty ?? l.amount;

        const lot = l.lot ?? l.lot_code ?? l.batch ?? l.batch_no ?? undefined;

        const expiry_date =
            l.expiry_date ??
            l.validity ??
            l.expiration ??
            l.expiration_date ??
            undefined;

        return {
            id: l.id ?? l.line_id ?? product_code ?? product_name,
            product_code,
            product_name,
            uom,
            qty,
            lot,
            expiry_date,
        };
    });
}

/** Componente ------------------------------------------------------------------ */
export default function OrderDetail() {
    const { id } = useParams<{ id: string }>();
    const orderId = Number(id);
    const [o, setO] = useState<Order | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");

    async function load() {
        if (!Number.isFinite(orderId)) return;
        setLoading(true);
        setError("");
        try {
            const data = await getOrder(orderId);
            setO(data);
        } catch (e: any) {
            setError(e?.message || "Falha ao carregar pedido");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId]);

    async function onEval() {
        try {
            setLoading(true);
            await evaluateOrder(orderId);
            await load();
            alert("Pedido avaliado.");
        } catch (e: any) {
            alert(e?.message || "Falha ao avaliar.");
        } finally {
            setLoading(false);
        }
    }

    async function onRelease() {
        try {
            setLoading(true);
            await releaseOrder(orderId);
            await load();
            alert("Pedido liberado.");
        } catch (e: any) {
            alert(e?.message || "Falha ao liberar.");
        } finally {
            setLoading(false);
        }
    }

    const lines: NormLine[] = o ? normalizeLines(o) : [];

    const createdRaw = o ? getCreatedAt(o) : undefined;
    const created = createdRaw ? new Date(createdRaw).toLocaleString() : "—";

    const reqRaw = o ? getRequireDate(o) : undefined;
    const req = reqRaw ? new Date(reqRaw).toLocaleDateString() : "—";

    return (
        <div className="p-4 md:p-6 max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-3">
                <h1 className="text-xl font-semibold">Pedido • Detalhe</h1>
                <Link to="/orders" className="text-sm underline opacity-80 hover:opacity-100">
                    Voltar para lista de pedidos
                </Link>
            </div>

            {loading && <div className="mb-3 opacity-70">Carregando…</div>}
            {!!error && (
                <div className="mb-3 px-3 py-2 rounded bg-red-900/30 text-red-200 border border-red-700">
                    {error}
                </div>
            )}

            {!o ? null : (
                <div className="space-y-6">
                    {/* Cabeçalho --------------------------------------------------------- */}
                    <div className="rounded-xl border border-neutral-800 p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                            <div>
                                <span className="opacity-70">Ref.: </span>
                                <span className="font-mono">{getRefCode(o)}</span>
                            </div>
                            <div>
                                <span className="opacity-70">Status: </span>
                                {getStatus(o)}
                            </div>
                            <div>
                                <span className="opacity-70">Cliente: </span>
                                {getCustomerDisplay(o)}
                            </div>
                            <div>
                                <span className="opacity-70">Data req.: </span>
                                {req}
                            </div>
                            <div>
                                <span className="opacity-70">Criado: </span>
                                {created}
                            </div>
                        </div>

                        <div className="mt-4 flex gap-2">
                            <button className="btn btn-outline btn-sm" onClick={onEval} disabled={loading}>
                                Avaliar
                            </button>
                            <button className="btn btn-outline btn-sm" onClick={onRelease} disabled={loading}>
                                Liberar
                            </button>
                        </div>
                    </div>

                    {/* Linhas ------------------------------------------------------------- */}
                    <div className="rounded-xl border border-neutral-800">
                        <div className="px-4 py-2 bg-neutral-900 text-neutral-100 text-sm">Linhas</div>
                        <div className="p-2 overflow-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr>
                                        <th className="text-left px-2 py-1">Produto</th>
                                        <th className="text-left px-2 py-1">UOM</th>
                                        <th className="text-left px-2 py-1">Qtd</th>
                                        <th className="text-left px-2 py-1">Lote</th>
                                        <th className="text-left px-2 py-1">Validade</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lines.map((l: NormLine, i: number) => (
                                        <tr key={(l.id ?? i) as React.Key} className="border-t border-neutral-800">
                                            <td className="px-2 py-1">
                                                <div className="font-mono">{l.product_code ?? "—"}</div>
                                                <div className="text-xs opacity-70">{l.product_name ?? ""}</div>
                                            </td>
                                            <td className="px-2 py-1">{l.uom ?? "—"}</td>
                                            <td className="px-2 py-1">{l.qty ?? "—"}</td>
                                            <td className="px-2 py-1">{l.lot ?? "—"}</td>
                                            <td className="px-2 py-1">
                                                {l.expiry_date ? new Date(l.expiry_date).toLocaleDateString() : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                    {lines.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="px-2 py-6 text-center opacity-70">
                                                Sem linhas.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
